/**
 * Real-time space documents: the board, sheets and pages of a space, kept as
 * Yjs documents on the server and edited by several people at once.
 *
 * Binary Yjs payloads (document updates, awareness updates, full states) ride
 * as standard base64 strings so the JSON RPC transport carries them as is.
 *
 * Wire contract:
 * - `spaces.subscribeDocument` streams one `sync` event (the full state, the
 *   session's access and the document's audience), then `update` and
 *   `awareness` events, and a final `closed` event when access is lost.
 * - `spaces.pushDocumentUpdate` stores an update before it acknowledges it
 *   with its sequence number, then sends it to the other subscribers.
 * - `spaces.pushAwareness` relays presence (carets, selections). The server
 *   writes the `user` field of every state, so nobody can pose as another.
 *
 * A missing document and one the session may not reach answer with the same
 * `SpaceDocumentNotFoundError`.
 *
 * @module spaceDocuments
 */
import * as Schema from "effect/Schema";

import { TrimmedNonEmptyString } from "./baseSchemas.ts";

/** `board`, or `sheet:<uuid>` / `page:<uuid>`. */
export const SPACE_DOCUMENT_ID_PATTERN = /^(board|(sheet|page):[0-9a-f-]{36})$/;

export const SPACE_DOCUMENT_KINDS = ["board", "sheet", "page"] as const;
export const SpaceDocumentKind = Schema.Literals(SPACE_DOCUMENT_KINDS);
export type SpaceDocumentKind = typeof SpaceDocumentKind.Type;

/** Who a document is for. `team` documents never reach a guest. */
export const SpaceDocumentAudience = Schema.Literals(["team", "client_view", "client_edit"]);
export type SpaceDocumentAudience = typeof SpaceDocumentAudience.Type;

/** What the session may do with the document it subscribed to. */
export const SpaceDocumentAccess = Schema.Literals(["edit", "view"]);
export type SpaceDocumentAccess = typeof SpaceDocumentAccess.Type;

/**
 * Size limits, in decoded bytes. The server enforces them; the client uses
 * them to avoid sending what the server would refuse.
 */
export const SPACE_DOCUMENT_LIMITS = {
  /** One pushed document update. */
  updateBytes: 1024 * 1024,
  /** One pushed awareness update. */
  awarenessBytes: 16 * 1024,
  /** The whole document state. */
  stateBytes: 20 * 1024 * 1024,
} as const;

const BASE64_PATTERN = /^[A-Za-z0-9+/]*={0,2}$/;

/**
 * A base64 payload. The cap is twice the encoded size of the largest update,
 * so a slightly larger update still reaches the server and gets the typed
 * `SpaceDocumentTooLargeError` instead of a decode failure.
 */
export const SpaceDocumentBase64 = Schema.String.check(
  Schema.isPattern(BASE64_PATTERN),
  Schema.isMaxLength(Math.ceil(SPACE_DOCUMENT_LIMITS.updateBytes / 3) * 4 * 2),
);
export type SpaceDocumentBase64 = typeof SpaceDocumentBase64.Type;

export const SpaceDocumentId = Schema.String.check(Schema.isPattern(SPACE_DOCUMENT_ID_PATTERN));
export type SpaceDocumentId = typeof SpaceDocumentId.Type;

const SpaceId = TrimmedNonEmptyString.check(Schema.isMaxLength(128));

/** One document of one space. */
export const SpaceDocumentRef = Schema.Struct({
  spaceId: SpaceId,
  docId: SpaceDocumentId,
});
export type SpaceDocumentRef = typeof SpaceDocumentRef.Type;

/**
 * The key of one subscription, from its `sync` event. A push that names it
 * is not sent back to that subscription, and awareness pushed under it is
 * removed when the subscription ends.
 */
const ConnectionKey = TrimmedNonEmptyString.check(Schema.isMaxLength(64));

export const SpaceDocumentPushUpdateInput = Schema.Struct({
  spaceId: SpaceId,
  docId: SpaceDocumentId,
  update: SpaceDocumentBase64,
  connection: Schema.optional(ConnectionKey),
});
export type SpaceDocumentPushUpdateInput = typeof SpaceDocumentPushUpdateInput.Type;

export const SpaceDocumentPushUpdateResult = Schema.Struct({
  /** The update's sequence number in the document's log. */
  seq: Schema.Int,
});
export type SpaceDocumentPushUpdateResult = typeof SpaceDocumentPushUpdateResult.Type;

export const SpaceDocumentPushAwarenessInput = Schema.Struct({
  spaceId: SpaceId,
  docId: SpaceDocumentId,
  update: SpaceDocumentBase64,
  connection: ConnectionKey,
});
export type SpaceDocumentPushAwarenessInput = typeof SpaceDocumentPushAwarenessInput.Type;

export const SpaceDocumentSyncEvent = Schema.Struct({
  type: Schema.Literal("sync"),
  /** The full document state, as one Yjs update. */
  state: Schema.String,
  access: SpaceDocumentAccess,
  audience: SpaceDocumentAudience,
  /** This subscription's key; name it in pushes. */
  connection: Schema.String,
});
export type SpaceDocumentSyncEvent = typeof SpaceDocumentSyncEvent.Type;

export const SpaceDocumentUpdateEvent = Schema.Struct({
  type: Schema.Literal("update"),
  update: Schema.String,
  /** The pushing subscription's key, `"server"` for a server edit, else null. */
  origin: Schema.NullOr(Schema.String),
  seq: Schema.Int,
});
export type SpaceDocumentUpdateEvent = typeof SpaceDocumentUpdateEvent.Type;

export const SpaceDocumentAwarenessEvent = Schema.Struct({
  type: Schema.Literal("awareness"),
  update: Schema.String,
});
export type SpaceDocumentAwarenessEvent = typeof SpaceDocumentAwarenessEvent.Type;

export const SpaceDocumentClosedEvent = Schema.Struct({
  type: Schema.Literal("closed"),
  reason: Schema.Literals(["access_lost"]),
});
export type SpaceDocumentClosedEvent = typeof SpaceDocumentClosedEvent.Type;

export const SpaceDocumentStreamEvent = Schema.Union([
  SpaceDocumentSyncEvent,
  SpaceDocumentUpdateEvent,
  SpaceDocumentAwarenessEvent,
  SpaceDocumentClosedEvent,
]);
export type SpaceDocumentStreamEvent = typeof SpaceDocumentStreamEvent.Type;

/** The document does not exist, or the session may not reach it. */
export class SpaceDocumentNotFoundError extends Schema.TaggedErrorClass<SpaceDocumentNotFoundError>()(
  "SpaceDocumentNotFoundError",
  { spaceId: Schema.String, docId: Schema.String },
) {
  override get message(): string {
    return "Document not found.";
  }
}

/** The session may read the document but not change it. */
export class SpaceDocumentReadOnlyError extends Schema.TaggedErrorClass<SpaceDocumentReadOnlyError>()(
  "SpaceDocumentReadOnlyError",
  { spaceId: Schema.String, docId: Schema.String },
) {
  override get message(): string {
    return "You can view this document but not edit it.";
  }
}

export class SpaceDocumentTooLargeError extends Schema.TaggedErrorClass<SpaceDocumentTooLargeError>()(
  "SpaceDocumentTooLargeError",
  { limitBytes: Schema.Int },
) {
  override get message(): string {
    return `The change is too large (limit ${this.limitBytes} bytes).`;
  }
}

/** The pushed bytes are not a Yjs update (or awareness update). */
export class SpaceDocumentInvalidUpdateError extends Schema.TaggedErrorClass<SpaceDocumentInvalidUpdateError>()(
  "SpaceDocumentInvalidUpdateError",
  {},
) {
  override get message(): string {
    return "The change could not be read.";
  }
}

export class SpaceDocumentRateLimitedError extends Schema.TaggedErrorClass<SpaceDocumentRateLimitedError>()(
  "SpaceDocumentRateLimitedError",
  { retryAfterMs: Schema.Int },
) {
  override get message(): string {
    return "Too many changes at once. Try again shortly.";
  }
}

/** Storage failed; nothing was stored or sent. Retry. */
export class SpaceDocumentUnavailableError extends Schema.TaggedErrorClass<SpaceDocumentUnavailableError>()(
  "SpaceDocumentUnavailableError",
  {},
) {
  override get message(): string {
    return "Documents are unavailable right now. Try again.";
  }
}

export const SpaceDocumentError = Schema.Union([
  SpaceDocumentNotFoundError,
  SpaceDocumentReadOnlyError,
  SpaceDocumentTooLargeError,
  SpaceDocumentInvalidUpdateError,
  SpaceDocumentRateLimitedError,
  SpaceDocumentUnavailableError,
]);
export type SpaceDocumentError = typeof SpaceDocumentError.Type;
